import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { Spinner, fmtPct, changeColor } from './UI';

const MARKET_SHORT = {
  MIL: 'Milano', NYSE: 'NYSE', NASDAQ: 'NASDAQ',
  XETRA: 'XETRA', PARIS: 'Parigi', LSE: 'Londra', RAW: 'Altro',
};

function scoreColor(s) {
  if (s >= 78) return '#10b981';
  if (s >= 65) return '#34d399';
  if (s >= 52) return '#fbbf24';
  return '#94a3b8';
}

function ScoreRing({ score }) {
  const c = scoreColor(score);
  const r = 20, circ = 2 * Math.PI * r;
  const offset = circ * (1 - score / 100);
  return (
    <div style={{ position: 'relative', width: 52, height: 52, flexShrink: 0 }}>
      <svg width="52" height="52" style={{ transform: 'rotate(-90deg)' }}>
        <circle cx="26" cy="26" r={r} fill="none" stroke="#1e2736" strokeWidth="4" />
        <circle cx="26" cy="26" r={r} fill="none" stroke={c} strokeWidth="4"
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round" />
      </svg>
      <div style={{
        position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
        justifyContent: 'center', fontSize: 15, fontWeight: 700, color: c,
        fontFamily: "'IBM Plex Mono', monospace",
      }}>{score}</div>
    </div>
  );
}

function HotRow({ item, rank, onOpen }) {
  const chg = item.change_5d_pct;
  return (
    <button onClick={() => onOpen(item)} className="hot-row" style={{
      display: 'flex', alignItems: 'center', gap: 16, width: '100%',
      padding: '14px 18px', background: 'transparent', border: 'none',
      borderBottom: '1px solid #161c28', cursor: 'pointer', textAlign: 'left',
    }}
      onMouseEnter={e => e.currentTarget.style.background = '#11161f'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
      <span className="rank" style={{ fontSize: 14, fontWeight: 700, color: '#334155', width: 24, fontFamily: "'IBM Plex Mono', monospace" }}>
        {rank}
      </span>
      <ScoreRing score={item.score} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: '#e2e8f0', fontFamily: "'IBM Plex Mono', monospace" }}>
            {item.ticker}
          </span>
          <span style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }}>
            {item.name}
          </span>
          <span style={{ fontSize: 10, color: '#475569', background: '#161c28', padding: '1px 7px', borderRadius: 20, flexShrink: 0 }}>
            {MARKET_SHORT[item.market] || item.market}
          </span>
        </div>
        <div className="reasons" style={{ fontSize: 12, color: '#64748b', lineHeight: 1.4 }}>
          {item.reasons && item.reasons.length > 0
            ? item.reasons.join(' · ')
            : 'Segnali tecnici nella media'}
        </div>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontSize: 13, color: '#e2e8f0', fontFamily: "'IBM Plex Mono', monospace" }}>
          {item.price}
        </div>
        <div style={{ fontSize: 12, color: changeColor(chg), fontFamily: "'IBM Plex Mono', monospace" }}>
          5g {fmtPct(chg)}
        </div>
      </div>
      <span className="analyze-link" style={{ fontSize: 13, color: '#3b82f6', flexShrink: 0 }}>Analizza →</span>
    </button>
  );
}

export default function ScreenerPage({ onOpen }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const load = () => {
    setLoading(true); setError(null);
    api.hotStocks(30)
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: '#f1f5f9', marginBottom: 6 }}>
          Opportunità di breve termine
        </h1>
        <p style={{ fontSize: 13, color: '#64748b', lineHeight: 1.6, maxWidth: 720 }}>
          Titoli ordinati per <strong style={{ color: '#94a3b8' }}>forza tecnica recente</strong> — una
          combinazione di trend, momentum, RSI, MACD, ADX, volumi e prossimità ai massimi.
          Il punteggio misura la forza dei segnali attuali, <strong style={{ color: '#94a3b8' }}>non
          è una previsione né un consiglio di investimento</strong>. Usalo per restringere il campo,
          poi approfondisci ogni titolo con l'analisi completa. Le decisioni e i rischi restano tuoi.
        </p>
      </div>

      {loading && (
        <div style={{ padding: '40px 0', textAlign: 'center' }}>
          <Spinner size={24} />
          <div style={{ fontSize: 13, color: '#475569', marginTop: 12 }}>
            Scansione di oltre 200 titoli su 6 borse in corso…<br />
            <span style={{ fontSize: 12 }}>La prima volta può richiedere fino a un minuto. I risultati restano in cache per un'ora.</span>
          </div>
        </div>
      )}

      {error && (
        <div style={{ padding: '12px 16px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, color: '#f87171', fontSize: 13 }}>
          ⚠ {error}
          <button onClick={load} style={{ marginLeft: 12, fontSize: 12, color: '#60a5fa', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
            Riprova
          </button>
        </div>
      )}

      {!loading && !error && data && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: '#475569' }}>
              {data.count} titoli analizzati · primi {data.items.length} per punteggio
            </span>
            <button onClick={load} style={{ fontSize: 12, color: '#60a5fa', background: 'none', border: '1px solid #1e2736', borderRadius: 6, padding: '4px 12px', cursor: 'pointer' }}>
              ↻ Aggiorna
            </button>
          </div>
          <div style={{ background: '#0d1117', border: '1px solid #1e2736', borderRadius: 12, overflow: 'hidden' }}>
            {data.items.map((item, i) => (
              <HotRow key={item.symbol} item={item} rank={i + 1} onOpen={onOpen} />
            ))}
          </div>
          <div style={{ fontSize: 11, color: '#334155', marginTop: 12, lineHeight: 1.5 }}>
            Punteggio 0–100 basato su soli dati di prezzo e volume. I fondamentali e il sentiment
            delle notizie non entrano in questo ranking ma sono disponibili nell'analisi completa di
            ciascun titolo. Dati con possibile ritardo, forniti da Yahoo Finance.
          </div>
        </>
      )}
    </div>
  );
}
